import { onConfigure } from './config';

/**
 * AIM messages were a small subset of HTML — bold, italic, underline, and font
 * color/size/face. Bodies are stored verbatim and parsed here at render time
 * into flat spans the RN <Text> tree can consume.
 *
 * Everything not on the allowlist is dropped, and no raw HTML ever reaches a
 * renderer, so a hostile body cannot inject markup or script.
 */

/**
 * A run of styled text, or an image. Images are their own span kind because
 * they are block-level in the transcript and carry no text.
 */
export type Span = TextSpan | ImageSpan;

export interface ImageSpan {
  kind: 'image';
  src: string;
  width?: number;
  height?: number;
  text?: undefined;
}

export interface TextSpan {
  kind?: 'text';
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  color?: string;
  size?: number;
  face?: string;
}

interface Style {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  color?: string;
  size?: number;
  face?: string;
}

/** HTML font sizes 1-7 mapped to the point sizes AIM actually rendered. */
const FONT_SIZES: Record<number, number> = { 1: 8, 2: 10, 3: 12, 4: 14, 5: 18, 6: 24, 7: 32 };

const NAMED_COLORS: Record<string, string> = {
  black: '#000000', white: '#ffffff', red: '#ff0000', green: '#008000',
  blue: '#0000ff', yellow: '#ffff00', purple: '#800080', gray: '#808080',
  grey: '#808080', orange: '#ffa500', pink: '#ffc0cb', cyan: '#00ffff',
  magenta: '#ff00ff', brown: '#a52a2a', navy: '#000080', teal: '#008080',
};

function safeColor(raw: string | undefined): string | undefined {
  if (!raw) return undefined;
  const v = raw.trim().toLowerCase();
  if (/^#[0-9a-f]{6}$/.test(v) || /^#[0-9a-f]{3}$/.test(v)) return v;
  return NAMED_COLORS[v];
}

function decodeEntities(s: string): string {
  return s
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&');
}

export function encodeEntities(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Matches anything tag-shaped, not just the tags we support. Unsupported tags
 * are dropped outright rather than left in the text — otherwise `<script>` and
 * friends would survive as literal characters and could become live markup
 * again downstream (a WebView, a notification body, an exported transcript).
 *
 * Quoted attribute values are consumed as units so a `>` inside one does not
 * terminate the tag early.
 */
const TAG_RE = /<\/?\s*([a-zA-Z][a-zA-Z0-9]*)((?:"[^"]*"|'[^']*'|[^>"'])*)\/?>/g;
const ALLOWED_TAGS = new Set(['b', 'i', 'u', 'em', 'strong', 'br', 'font', 'img']);
const ATTR_RE = /([a-z-]+)\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'>]+))/gi;

/**
 * Origin that `<img src>` may point at. Set from the deployed uploads CDN.
 *
 * This is a security control, not a convenience. A message body is written by
 * another user, so an unrestricted `<img src>` lets a sender embed a remote URL
 * and learn the recipient's IP address, rough location and online time the
 * instant the IM is opened — silently, with no interaction. Restricting src to
 * our own host means fetching an image tells the sender nothing.
 */
let allowedImageOrigin: string | null = null;

export function setAllowedImageOrigin(origin: string | null): void {
  allowedImageOrigin = origin ? origin.replace(/\/$/, '') : null;
}

// Bind the allowlist whenever the app is configured, so no caller has to
// remember. Tests call setAllowedImageOrigin directly.
onConfigure((config) => setAllowedImageOrigin(config.uploadsUrl || null));

/** Narrows a span to an image. */
export function isImageSpan(span: Span): span is ImageSpan {
  return span.kind === 'image';
}

/** Narrows a span to styled text. */
export function isTextSpan(span: Span): span is TextSpan {
  return span.kind !== 'image';
}

/** True if `src` is an image on our uploads origin. */
export function isAllowedImageSrc(src: string): boolean {
  if (!allowedImageOrigin) return false;
  const url = src.trim();
  // Must match the origin exactly and then continue with a path separator, so
  // "https://uploads.example.com.evil.test/x" cannot pass a prefix check.
  if (!url.startsWith(allowedImageOrigin + '/')) return false;
  // No credentials, no port tricks, no protocol-relative or javascript: URLs —
  // startsWith already forces https://<host>/, this rejects the rest.
  return !/[\\<>"'\s]/.test(url) && !url.includes('@');
}

export function parseRichText(body: string): Span[] {
  const spans: Span[] = [];
  const stack: Style[] = [{ bold: false, italic: false, underline: false }];
  const top = () => stack[stack.length - 1]!;

  const push = (text: string) => {
    if (!text) return;
    const s = top();
    // Only set keys that actually apply, so a span carries no undefined noise
    // and is cheap to compare in tests and memoized renderers.
    const span: TextSpan = { text: decodeEntities(text) };
    if (s.bold) span.bold = true;
    if (s.italic) span.italic = true;
    if (s.underline) span.underline = true;
    if (s.color) span.color = s.color;
    if (s.size) span.size = s.size;
    if (s.face) span.face = s.face;
    spans.push(span);
  };

  let cursor = 0;
  let m: RegExpExecArray | null;
  TAG_RE.lastIndex = 0;

  while ((m = TAG_RE.exec(body)) !== null) {
    push(body.slice(cursor, m.index));
    cursor = m.index + m[0].length;

    const tag = m[1]!.toLowerCase();
    const attrs = m[2] ?? '';
    const closing = m[0].startsWith('</');

    // Anything not on the allowlist is discarded, tag and all.
    if (!ALLOWED_TAGS.has(tag)) continue;

    if (tag === 'br') {
      push('\n');
      continue;
    }

    if (tag === 'img') {
      if (closing) continue;
      const a = readAttrs(attrs);
      // Dropped silently when the src is not ours: rendering a placeholder
      // would still tell the sender the message was opened.
      if (a.src && isAllowedImageSrc(a.src)) {
        const width = a.width ? parseInt(a.width, 10) : undefined;
        const height = a.height ? parseInt(a.height, 10) : undefined;
        const span: ImageSpan = { kind: 'image', src: a.src.trim() };
        if (width && width > 0 && width <= 4096) span.width = width;
        if (height && height > 0 && height <= 4096) span.height = height;
        spans.push(span);
      }
      continue;
    }

    if (closing) {
      // Never pop the base style; a stray </b> should not corrupt the rest.
      if (stack.length > 1) stack.pop();
      continue;
    }

    const next: Style = { ...top() };
    if (tag === 'b' || tag === 'strong') next.bold = true;
    else if (tag === 'i' || tag === 'em') next.italic = true;
    else if (tag === 'u') next.underline = true;
    else if (tag === 'font') {
      const parsed = readAttrs(attrs);
      for (const [name, value] of Object.entries(parsed)) {
        if (name === 'color') next.color = safeColor(value) ?? next.color;
        else if (name === 'size') {
          const n = parseInt(value, 10);
          if (n >= 1 && n <= 7) next.size = FONT_SIZES[n];
        } else if (name === 'face') {
          // Font faces are matched against what the app bundles; an arbitrary
          // string is harmless because RN falls back to the system font.
          next.face = value.split(',')[0]?.trim() || undefined;
        }
      }
    }
    // Depth cap: a body of 10,000 nested <b> tags should not blow the stack.
    if (stack.length < 32) stack.push(next);
  }

  push(body.slice(cursor));
  // Drop empty text runs, but never images — they carry no text by definition.
  return spans.filter((s) => s.kind === 'image' || (s.text?.length ?? 0) > 0);
}

/** Wraps plain user input in the tags implied by the composer's toolbar state. */
export function buildRichText(
  text: string,
  style: { bold?: boolean; italic?: boolean; underline?: boolean; color?: string; size?: number }
): string {
  let body = encodeEntities(text).replace(/\r?\n/g, '<br>');
  if (style.color || style.size) {
    const attrs = [
      style.color ? `color="${safeColor(style.color) ?? '#000000'}"` : '',
      style.size ? `size="${style.size}"` : '',
    ]
      .filter(Boolean)
      .join(' ');
    body = `<font ${attrs}>${body}</font>`;
  }
  if (style.underline) body = `<u>${body}</u>`;
  if (style.italic) body = `<i>${body}</i>`;
  if (style.bold) body = `<b>${body}</b>`;
  return body;
}

/** Attributes of one tag, lowercased names, as a plain object. */
function readAttrs(attrs: string): Record<string, string> {
  const out: Record<string, string> = {};
  ATTR_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = ATTR_RE.exec(attrs)) !== null) {
    out[m[1]!.toLowerCase()] = m[2] ?? m[3] ?? m[4] ?? '';
  }
  return out;
}

/** Wraps an uploaded image for embedding in a body. */
export function buildImageTag(src: string, width?: number, height?: number): string {
  if (!isAllowedImageSrc(src)) {
    throw new Error('Images must be uploaded to og-aim before they can be sent.');
  }
  const dims = [
    width ? ` width="${Math.round(width)}"` : '',
    height ? ` height="${Math.round(height)}"` : '',
  ].join('');
  return `<img src="${encodeURI(src)}"${dims}>`;
}

/** Plain-text rendering, for notifications and the buddy-list preview. */
export function toPlainText(body: string): string {
  return parseRichText(body)
    .map((s) => (s.kind === 'image' ? '[image]' : s.text))
    .join('');
}

/** True if the body contains at least one renderable image. */
export function hasImage(body: string): boolean {
  return parseRichText(body).some((s) => s.kind === 'image');
}
